import { EscPos } from "./escpos";
import { api } from "./api";
import { IS_DESKTOP } from "./desktop";

const BT_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
  "0000ae30-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
];

export const SPP_UUID = "00001101-0000-1000-8000-00805f9b34fb";
export const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

export const support = () => ({
  bluetooth: typeof navigator !== "undefined" && !!navigator.bluetooth,
  usb: typeof navigator !== "undefined" && !!navigator.usb,
  serial: typeof navigator !== "undefined" && !!navigator.serial,
  spp: typeof navigator !== "undefined" && !!navigator.serial,
  rawbt: isAndroid(),
  windows: IS_DESKTOP,
});

const toB64 = (d) => {
  let s = "";
  for (let i = 0; i < d.length; i += 8192) s += String.fromCharCode.apply(null, d.subarray(i, i + 8192));
  return btoa(s);
};

const windowsConn = ({ printer = "", usb = "", name }) => ({
  name: name || printer,
  info: { printer, usb, name: name || printer },
  write: async (d) => {
    try {
      await api.post("/desktop/print", { printer, usb, data: toB64(d instanceof Uint8Array ? d : new Uint8Array(d)) });
    } catch (e) {
      throw new Error(e.response?.data?.detail || e.message);
    }
  },
  close: async () => {},
});

const rawbtConn = () => ({
  name: "RawBT (Android)",
  info: {},
  write: async (d) => {
    let s = "";
    for (let i = 0; i < d.length; i++) s += String.fromCharCode(d[i]);
    window.location.href = "intent:base64," + btoa(s) + "#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;";
  },
  close: async () => {},
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function openSerial(port, baudRate) {
  await port.open({ baudRate: Number(baudRate) || 9600 });
  const writer = port.writable.getWriter();
  const info = port.getInfo ? port.getInfo() : {};
  return {
    name: info.usbVendorId ? `Serial ${info.usbVendorId.toString(16)}:${(info.usbProductId || 0).toString(16)}` : "Serial / COM",
    info,
    write: async (d) => {
      await writer.ready;
      await writer.write(d);
      await writer.ready;
    },
    close: async () => {
      writer.releaseLock();
      await port.close();
    },
  };
}

async function openUsb(device) {
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  let ep = null;
  let ifaceNum = null;
  for (const iface of device.configuration.interfaces) {
    for (const alt of iface.alternates) {
      const out = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
      if (out) {
        ep = out;
        ifaceNum = iface.interfaceNumber;
        break;
      }
    }
    if (ep) break;
  }
  if (!ep) throw new Error("Endpoint printer USB tidak ditemukan");
  await device.claimInterface(ifaceNum);
  return {
    name: device.productName || "USB Printer",
    info: { vendorId: device.vendorId, productId: device.productId },
    write: async (d) => {
      for (let i = 0; i < d.length; i += 4096) await device.transferOut(ep.endpointNumber, d.slice(i, i + 4096));
    },
    close: () => device.close(),
  };
}

async function openBluetooth(device, onDisconnect) {
  const server = await device.gatt.connect();
  const services = (await server.getPrimaryServices()).filter((s) => !/^0000180[01]-/.test(s.uuid));
  services.sort((a, b) => (BT_SERVICES.indexOf(a.uuid) + 1 || 99) - (BT_SERVICES.indexOf(b.uuid) + 1 || 99));
  let ch = null;
  for (const s of services) {
    const chars = await s.getCharacteristics();
    ch = chars.find((c) => c.properties.writeWithoutResponse) || chars.find((c) => c.properties.write);
    if (ch) break;
  }
  if (!ch) throw new Error("Printer Bluetooth tidak mendukung penulisan data (BLE)");
  device.addEventListener("gattserverdisconnected", onDisconnect);
  return {
    name: device.name || "Bluetooth Printer",
    info: { id: device.id },
    write: async (d) => {
      for (let i = 0; i < d.length; i += 180) {
        const chunk = d.slice(i, i + 180);
        if (ch.properties.writeWithoutResponse) await ch.writeValueWithoutResponse(chunk);
        else await ch.writeValue(chunk);
        await sleep(15);
      }
    },
    close: async () => device.gatt.disconnect(),
  };
}

class HardwareManager {
  conns = { printer: null, drawer: null, scanner: null };
  subs = new Set();
  scanSubs = new Set();

  state() {
    const s = {};
    for (const k of Object.keys(this.conns)) {
      const c = this.conns[k];
      s[k] = c ? { type: c.type, name: c.name } : null;
    }
    return s;
  }
  emit() {
    const s = this.state();
    this.subs.forEach((f) => f(s));
  }
  subscribe(f) {
    this.subs.add(f);
    return () => this.subs.delete(f);
  }
  onScan(f) {
    this.scanSubs.add(f);
    return () => this.scanSubs.delete(f);
  }
  save(role, type, info, baud) {
    const all = JSON.parse(localStorage.getItem("pos_hw_devices") || "{}");
    all[role] = { type, info, baud };
    localStorage.setItem("pos_hw_devices", JSON.stringify(all));
  }
  async set(role, type, conn, baud) {
    if (this.conns[role]) await this.disconnect(role, true);
    this.conns[role] = { ...conn, type };
    this.save(role, type, conn.info, baud);
    this.emit();
    return this.state()[role];
  }
  async connect(role, type, baud = 9600, opts = {}) {
    let conn;
    if (type === "windows") {
      if (!opts.printer && !opts.usb) throw new Error("Pilih printer terlebih dahulu");
      conn = windowsConn(opts);
    } else if (type === "bluetooth") {
      const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: BT_SERVICES });
      conn = await openBluetooth(device, () => this.lost(role));
    } else if (type === "usb") {
      const device = await navigator.usb.requestDevice({ filters: [] });
      conn = await openUsb(device);
    } else if (type === "rawbt") {
      conn = rawbtConn();
    } else if (type === "spp") {
      const port = await navigator.serial.requestPort({ filters: [{ bluetoothServiceClassId: SPP_UUID }], allowedBluetoothServiceClassIds: [SPP_UUID] });
      conn = role === "scanner" ? await this.openSerialScanner(port, baud) : await openSerial(port, baud);
      conn.name = "Bluetooth SPP" + (role === "printer" ? " Printer" : "");
    } else {
      const port = await navigator.serial.requestPort();
      if (role === "scanner") conn = await this.openSerialScanner(port, baud);
      else conn = await openSerial(port, baud);
    }
    return this.set(role, type, conn, baud);
  }
  async openSerialScanner(port, baud) {
    await port.open({ baudRate: Number(baud) || 9600 });
    const decoder = new TextDecoderStream();
    const closed = port.readable.pipeTo(decoder.writable).catch(() => {});
    const reader = decoder.readable.getReader();
    let alive = true;
    (async () => {
      let buf = "";
      while (alive) {
        const { value, done } = await reader.read().catch(() => ({ done: true }));
        if (done) break;
        buf += value;
        const parts = buf.split(/\r?\n|\r/);
        buf = parts.pop();
        parts.map((p) => p.trim()).filter(Boolean).forEach((code) => this.scanSubs.forEach((f) => f(code)));
      }
    })();
    return {
      name: "Scanner Serial",
      info: port.getInfo ? port.getInfo() : {},
      write: async () => {},
      close: async () => {
        alive = false;
        await reader.cancel().catch(() => {});
        await closed;
        await port.close().catch(() => {});
      },
    };
  }
  lost(role) {
    this.conns[role] = null;
    this.emit();
  }
  async disconnect(role, keepSaved = false) {
    const c = this.conns[role];
    this.conns[role] = null;
    if (c) await c.close().catch(() => {});
    if (!keepSaved) {
      const all = JSON.parse(localStorage.getItem("pos_hw_devices") || "{}");
      delete all[role];
      localStorage.setItem("pos_hw_devices", JSON.stringify(all));
    }
    this.emit();
  }
  async autoReconnect() {
    const all = JSON.parse(localStorage.getItem("pos_hw_devices") || "{}");
    for (const [role, saved] of Object.entries(all)) {
      if (this.conns[role]) continue;
      try {
        if (saved.type === "windows" && IS_DESKTOP && (saved.info?.printer || saved.info?.usb)) {
          await this.set(role, "windows", windowsConn(saved.info), saved.baud);
        } else if (saved.type === "usb" && navigator.usb) {
          const devs = await navigator.usb.getDevices();
          const d = devs.find((x) => x.vendorId === saved.info?.vendorId && x.productId === saved.info?.productId);
          if (d) await this.set(role, "usb", await openUsb(d), saved.baud);
        } else if (saved.type === "rawbt" && isAndroid()) {
          await this.set(role, "rawbt", rawbtConn(), saved.baud);
        } else if ((saved.type === "serial" || saved.type === "spp") && navigator.serial) {
          const ports = await navigator.serial.getPorts();
          const p =
            ports.find((x) => x.getInfo().usbVendorId === saved.info?.usbVendorId && x.getInfo().usbProductId === saved.info?.usbProductId) ||
            (ports.length === 1 ? ports[0] : null);
          if (p) {
            const conn = role === "scanner" ? await this.openSerialScanner(p, saved.baud) : await openSerial(p, saved.baud);
            await this.set(role, saved.type, conn, saved.baud);
          }
        } else if (saved.type === "bluetooth" && navigator.bluetooth?.getDevices) {
          const devs = await navigator.bluetooth.getDevices();
          const d = devs.find((x) => x.id === saved.info?.id);
          if (d) await this.set(role, "bluetooth", await openBluetooth(d, () => this.lost(role)), saved.baud);
        }
      } catch (e) {
        /* device not available now; user can reconnect manually */
      }
    }
  }
  async print(bytes) {
    const c = this.conns.printer;
    if (!c) throw new Error("Printer belum terhubung");
    await c.write(bytes);
  }
  async openDrawer(via = "printer", pin = 0) {
    const c = via === "serial" ? this.conns.drawer : this.conns.printer;
    if (!c) throw new Error(via === "serial" ? "Laci uang (serial) belum terhubung" : "Printer belum terhubung — laci RJ11 dibuka lewat printer. Hubungkan printer struk terlebih dahulu.");
    await c.write(new EscPos().drawer(pin).bytes());
  }
}

export const hardware = new HardwareManager();
